import { z } from 'zod';
import { FieldPerfRangeSchema, type FieldPerfRange } from './fieldPerf.schema.js';

/* ===========================================================================
 * THE BATTERY LIFECYCLE — GET /api/field-perf/battery?range=24h (owner,
 * 2026-10-29: "add battery lifecycle").
 *
 * Per device over the Performance section's own range: the level over time,
 * the charge sessions, the drain while unplugged, the lowest point, the time
 * spent on the low and critical floors (`BATTERY_LOW_PERCENT`,
 * `BATTERY_CRITICAL_PERCENT` — deviceBattery.ts), and charges per COMPANY day.
 *
 * Built from two sources merged by time: `DeviceBatteryReading` (the throttled
 * history every sync request feeds) and the battery a scheduled GPS reading
 * carries (`DeviceGpsSample.batteryPct` / `charging` / `screenOn`).
 *
 * NULL IS "NOT KNOWN", never 0 (design-rules §3.3): a device with fewer than
 * two readings in the range has no drain, no time on a floor and no lowest
 * point to state.
 * ======================================================================== */

export const FieldBatteryQuerySchema = z.object({
  range: FieldPerfRangeSchema.default('24h'),
});

export interface FieldBatteryPoint {
  at: string;
  percent: number;
  charging: boolean;
}

export interface FieldChargeSession {
  /** The first reading on the cable. */
  start: string;
  /** False when the range opened with the device already charging — `start`
   *  is then only when the range first saw it, and the session is not
   *  counted as a charge in `chargesByDay`. */
  startKnown: boolean;
  /** The first reading off the cable; null = still charging at the last reading. */
  end: string | null;
  fromPercent: number;
  toPercent: number;
}

export interface FieldBatteryDayCharges {
  /** Company calendar day, YYYY-MM-DD. */
  day: string;
  charges: number;
}

export interface FieldBatteryDeviceRow {
  deviceId: string;
  /** What the device last reported, whenever that was (Device.battery*).
   *  Null = it has never reported a battery. */
  current: { percent: number; charging: boolean; at: string } | null;
  /** Readings in the range, both sources merged. */
  readings: number;
  /** The level over the range, oldest first, thinned to at most
   *  BATTERY_SERIES_MAX_POINTS for a sparkline. */
  series: FieldBatteryPoint[];
  sessions: FieldChargeSession[];
  /** Percentage points lost per hour while unplugged; null when the range
   *  holds too little unplugged time to say. */
  drainPerHour: number | null;
  lowestPercent: number | null;
  /** Time at or under BATTERY_LOW_PERCENT / BATTERY_CRITICAL_PERCENT, in ms. */
  lowMs: number | null;
  criticalMs: number | null;
  /** Every company day the range touches, oldest first. */
  chargesByDay: FieldBatteryDayCharges[];
  /** Charges whose start is known, per 24 h of range; null without readings. */
  chargesPerDay: number | null;
  /** Of the GPS readings that said, the share taken with the screen on —
   *  why one tablet drains faster than its neighbour. Null = none said. */
  screenOnShare: number | null;
}

export interface FieldBatteryResponse {
  range: FieldPerfRange;
  generatedAt: string;
  rows: FieldBatteryDeviceRow[];
}

/** The sparkline's resolution: enough to see a day's charge-and-drain. */
export const BATTERY_SERIES_MAX_POINTS = 96;
